import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
//import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Chain,
  Redo,
  //Sheet,
  TextBold,
  TextItalic,
  TextUnderline,
  Undo,
} from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '../../../Button';
import { getSelectedNode } from '../RichTextArea.utils';

import styles from './ToolbarPlugin.module.scss';
import { ToolbarPluginProps } from './ToolbarPlugin.types';

const LowPriority = 1;

export const ToolbarPlugin = ({ setIsLinkEditOpen, toolbarChildren }: ToolbarPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [align, setAlign] = useState('left');
  const [isLink, setIsLink] = useState(false);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(
          node,
          parentNode => $isElementNode(parentNode) && !parentNode.isInline()
        );
      }
      setAlign(
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType() || 'left'
          : $isElementNode(node)
            ? node.getFormatType() || 'left'
            : parent?.getFormatType() || 'left'
      );
      setIsLink($isLinkNode(parent) || $isLinkNode(node));
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        payload => {
          setCanUndo(payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        payload => {
          setCanRedo(payload);
          return false;
        },
        LowPriority
      )
    );
  }, [editor, $updateToolbar]);

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarContent}>
        <Button
          variant="tertiary"
          size="small"
          disabled={!canUndo}
          onClick={() => {
            editor.dispatchCommand(UNDO_COMMAND, undefined);
          }}
        >
          <Undo />
        </Button>
        <Button
          variant="tertiary"
          size="small"
          disabled={!canRedo}
          onClick={() => {
            editor.dispatchCommand(REDO_COMMAND, undefined);
          }}
        >
          <Redo />
        </Button>
        <div className={styles.divider} />
        <Button
          variant="tertiary"
          size="small"
          selected={isBold}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
          }}
        >
          <TextBold />
        </Button>
        <Button
          variant="tertiary"
          size="small"
          selected={isItalic}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          }}
        >
          <TextItalic />
        </Button>
        <Button
          variant="tertiary"
          size="small"
          selected={isUnderline}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
          }}
        >
          <TextUnderline />
        </Button>
        <div className={styles.divider} />
        <Button
          variant="tertiary"
          size="small"
          selected={align === 'left'}
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
          }}
        >
          <AlignLeft />
        </Button>
        <Button
          variant="tertiary"
          size="small"
          selected={align === 'center'}
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
          }}
        >
          <AlignCenter />
        </Button>
        <Button
          variant="tertiary"
          size="small"
          selected={align === 'right'}
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
          }}
        >
          <AlignRight />
        </Button>
        <div className={styles.divider} />
        <Button
          variant="tertiary"
          size="small"
          selected={isLink}
          onClick={() => {
            if (isLink) {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
              setIsLinkEditOpen(false);
            } else {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url: 'https://' });
              setIsLinkEditOpen(true);
            }
          }}
        >
          <Chain />
        </Button>
        {/* <Button    //TODO: Uncomment when table plugin is ready
          variant="tertiary"
          size="small"
          selected={isLink}
          onClick={() => {
            editor.dispatchCommand(INSERT_TABLE_COMMAND, {
              columns: '4',
              rows: '4',
              includeHeaders: { rows: true, columns: false },
            });
          }}
        >
          <Sheet />
        </Button>*/}
      </div>
      {toolbarChildren ? <div className={styles.toolbarChildren}>{toolbarChildren}</div> : null}
    </div>
  );
};
